import { differenceInSeconds } from 'date-fns';
import AuthService, { AuthEvents } from './AuthService';

export type PollTickHandler = (elapsedTime: number) => void;

export default class Poll {
  private active = false;

  private attached = false;

  private latestTimestamp = 0;

  private tickHandler: PollTickHandler | null;

  private ticks = 0;

  private timeout: ReturnType<typeof setTimeout> | null = null;

  public constructor(tickHandler: PollTickHandler) {
    this.attach();
    this.tickHandler = tickHandler;
  }

  public pause() {
    this.tick();
    this.active = false;
  }

  public resume() {
    // Avoid restarting the poll twice if it's already active.
    if (this.active) {
      return;
    }

    this.active = true;
    this.restart();
    this.poll();
  }

  /**
   * Removes the listeners to avoid unwanted ticks to be triggered if
   * a component is unmounted or the user navigates backs and forth between
   * pages that contain elements depending on this poll.
   */
  public detach() {
    if (this.attached) {
      AuthService.off(AuthEvents.BeforeSignOut, this.tick.bind(this));
      window.removeEventListener('beforeunload', this.tick.bind(this));
    }
  }

  // Stops the poll and clears the timeout
  public stop() {
    this.pause();
    this.detach();
    this.latestTimestamp = 0;
    this.ticks = 0;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  /**
   * Adds listeners to the events that may cause an interruption for the
   * user session, such as logging out, leaving the page, closing or changing
   * the browser's active tab. In these scenarios, the poll ticks
   * immediately to avoid data loss.
   */
  private attach() {
    AuthService.on(AuthEvents.BeforeSignOut, this.tick.bind(this));
    window.addEventListener('beforeunload', this.tick.bind(this));
    this.attached = true;
  }

  private poll() {
    this.timeout = setTimeout(() => {
      this.tick();
      this.poll();
    }, this.getTickInterval());
  }

  /**
   * Resets the latest timestamp of the poll and clears the timeout to avoid
   * overlapping polling timeouts. This is important to properly calculate
   * the elapsed time since the latest tick.
   */
  private restart() {
    this.latestTimestamp = Date.now();
    this.ticks = 0;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  /**
   * A poll tick could represent 2 main scenarios:
   *  1. The poll interval is completed
   *  2. The poll has been paused
   * The latter triggers a tick immediately since we would like to measure
   * the ellapsed time since the latest tick and the moment the poll has been
   * paused.
   */
  private tick() {
    const pastTimestamp = this.latestTimestamp;
    this.latestTimestamp = Date.now();
    this.ticks++;

    if (!(this.tickHandler && this.active)) {
      return;
    }

    const elapsedTime = differenceInSeconds(
      this.latestTimestamp,
      pastTimestamp
    );

    // avoid passing to higher layers the responsibility to handle the scenario
    // where there are 0 elapsed seconds, since most cases will simply ignore it
    if (elapsedTime > 0) {
      this.tickHandler(elapsedTime);
    }
  }

  /**
   * Returns an incremental interval according to the amount of ticks, up to
   * a maximum of 30 seconds per tick. Following the series:
   *  n = 0 ...4, 6 ... 10, 11 ... 15, ...,  25 ... m
   *  i =    5  ,    10   ,    15    , ...,      30
   * Notice that if the ticks are reset (e.g.: poll is paused and then resumed),
   * the interval will reset as well.
   */
  private getTickInterval() {
    const n = this.ticks > 25 ? 25 : this.ticks;
    return (5 + 5 * Math.floor(n / 5)) * 1000;
  }
}
